// GAVION ai-email (docs/superpowers/specs/2026-10-14-ai-email-assistant-design.md): the AI assistant's shared contract.
// Pure: imports types only, so the app (fake drafter, demo) can import it. Haiku 5.5 by default; Sonnet 5.5 writes the
// follow-up touches (spec 2026-10-20-ai-follow-up-agent-design §3).
import type { CompanyFact } from "./companyContextDefault.ts";
import type { FollowUpAngle, FollowUpFacts } from "./followUp.ts";
export const AI_MODEL = "claude-haiku-5-5";
export const AI_MAX_TOKENS = 4000; // adaptive thinking shares this cap with the answer (ruling)
/** GAVION ai follow-up (spec §3): Sonnet 5.5 writes the follow-up touches; Haiku 5.5 stays the default elsewhere. */
export const AI_MODEL_SONNET = "claude-sonnet-5-5";
export const AI_MODELS = [AI_MODEL, AI_MODEL_SONNET] as const;
export type AiModel = (typeof AI_MODELS)[number];
export const isAiModel = (v: unknown): v is AiModel =>
  (AI_MODELS as readonly unknown[]).includes(v);
/** Sonnet thinks longer before it answers: a larger cap, shared by adaptive thinking and the answer. */
export const SONNET_MAX_TOKENS = 8000;
export const maxTokensFor = (m: AiModel): number =>
  m === AI_MODEL_SONNET ? SONNET_MAX_TOKENS : AI_MAX_TOKENS;
/**
 * Per-request SDK options (anthropicClient.ts): Sonnet thinks longer, so 60 s and no SDK retry (the tick retries a touch
 * 10 minutes later), which keeps one tick bounded; Haiku keeps the client defaults (30 s, 2 retries) unless the caller
 * asks for no SDK retry (`noRetry`: the follow-up touches, whose retry is the queue's own, 10 minutes later).
 */
export const SONNET_TIMEOUT_MS = 60_000;
/** The client defaults (anthropicClient.ts), used by Haiku: 30 s per attempt, 2 SDK retries. */
export const AI_TIMEOUT_MS = 30_000;
export const AI_MAX_RETRIES = 2;
/**
 * The longest wait between two SDK attempts: its own backoff is capped at 8 s, but it honours a 429/529 retry-after
 * header up to 60 s.
 */
export const AI_RETRY_WAIT_MAX_MS = 60_000;
/** `timeoutMs` (GAVION call-brief-pipeline): this call's own SDK timeout, never retried by the SDK. */
export type CallOptions = { noRetry?: boolean; timeoutMs?: number };
export function requestOptionsFor(
  model: AiModel,
  opts: CallOptions = {},
): { timeout: number; maxRetries: number } | undefined {
  if (opts.timeoutMs !== undefined)
    return { timeout: opts.timeoutMs, maxRetries: 0 }; // GAVION call-brief-pipeline
  if (model === AI_MODEL_SONNET)
    return { timeout: SONNET_TIMEOUT_MS, maxRetries: 0 };
  return opts.noRetry ? { timeout: AI_TIMEOUT_MS, maxRetries: 0 } : undefined;
}
/**
 * The longest one model call can take, SDK retries and retry-after waits included: Sonnet 60 s (never retried); Haiku
 * 3 × 30 s + 2 × 60 s = 210 s, or 30 s with `noRetry`. The follow-up tick calls with `noRetry` and never starts a touch
 * that could not finish within the edge's request timeout (followUpTick.ts).
 */
export function worstCaseMsFor(model: AiModel, opts: CallOptions = {}): number {
  const o = requestOptionsFor(model, opts);
  const retries = o?.maxRetries ?? AI_MAX_RETRIES;
  return (
    (o?.timeout ?? AI_TIMEOUT_MS) * (retries + 1) +
    retries * AI_RETRY_WAIT_MAX_MS
  );
}
/** Spec §3: a missing fact is left as « [à compléter] » (EN variant « [to fill in] »); the UI blocks the send. */
export const AI_PLACEHOLDERS = ["[à compléter]", "[to fill in]"] as const;

export type Lang = "fr" | "en";
export type ProposedTrigger =
  | "after_call"
  | "client_reply"
  | "after_demo"
  | "stale_lead";
export type DraftTrigger = ProposedTrigger | "on_demand" | "follow_up"; // GAVION ai follow-up
export const PROPOSED_TRIGGERS: readonly ProposedTrigger[] = [
  "after_call",
  "client_reply",
  "after_demo",
  "stale_lead",
];

export type DraftOutput = {
  subject: string;
  body: string;
  language: Lang;
  rationale: string;
  /** Follow-up touches (spec §3): the playbook angle the model wrote for. */
  angle?: FollowUpAngle;
};
export type StyleOutput = { profile_text: string; excerpts: string[] };

export type TextBlock = {
  type: "text";
  text: string;
  cache_control?: { type: "ephemeral" };
};
/**
 * GAVION ai-hiring-review: a PDF sent to the model as a document block (base64). `text` is never set: it keeps
 * `block.text` readable on the TextBlock | DocumentBlock union.
 */
export type DocumentBlock = {
  type: "document";
  source: { type: "base64"; media_type: "application/pdf"; data: string };
  title?: string;
  text?: undefined;
};
export type ModelRequest = {
  model: AiModel;
  max_tokens: number;
  system: TextBlock[];
  /** GAVION ai-hiring-review: a CV may come as a PDF document block. */
  messages: { role: "user"; content: (TextBlock | DocumentBlock)[] }[];
  /** GAVION call-brief-pipeline: Haiku 5.5 thinks by default; the 300-token brief turns it off. */
  thinking?: { type: "disabled" };
  output_config: {
    effort: "low" | "medium";
    /** GAVION ops-lookup: absent with web search (structured outputs refuse citations); the answer is parsed. */
    format?: { type: "json_schema"; schema: Record<string, unknown> };
  };
  /** GAVION ops-lookup: Anthropic's server-side web search (the only tool any request carries). */
  tools?: WebSearchTool[];
};
/** GAVION ops-lookup: the basic web search server tool (Haiku 5.5 and its Sonnet 5.5 fallback). */
export type WebSearchTool = {
  type: "web_search_20250305";
  name: "web_search";
  max_uses?: number;
};
/** GAVION ops-lookup: one result of a web_search_tool_result block (its page content stays encrypted). */
export type WebSearchResult = {
  type: "web_search_result";
  url: string;
  title?: string | null;
  page_age?: string | null;
};
/** GAVION ops-lookup: a text block's citation of a search result. */
export type WebCitation = {
  type: string;
  url?: string | null;
  title?: string | null;
  cited_text?: string | null;
};
/** A response content block: text (maybe with citations), or the web search's server_tool_use / web_search_tool_result. */
export type ModelContentBlock = {
  type: string;
  text?: string;
  citations?: WebCitation[] | null;
  /** web_search_tool_result: the results, or an error object ({type: "web_search_tool_result_error", error_code}). */
  content?: unknown;
};
export type ModelUsage = {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens?: number | null;
  cache_creation_input_tokens?: number | null;
  /** GAVION ops-lookup: the searches the web search tool ran ($10 per 1,000, cost.ts). */
  server_tool_use?: { web_search_requests?: number | null } | null;
};
export type ModelResponse = {
  model: string;
  stop_reason: string | null;
  content: ModelContentBlock[];
  usage: ModelUsage;
};
/** The one seam to the model: anthropicClient.ts in the edge, a fake in tests. */
export interface ModelClient {
  create(req: ModelRequest, opts?: CallOptions): Promise<ModelResponse>;
}
export type UsageFigures = {
  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  cost_usd: number;
};
export type AiErrorCode =
  | "refusal"
  | "rate_limited"
  | "unavailable"
  | "timeout"
  | "invalid_output"
  | "unsupported_claim"
  | "bad_request"
  | "config_error";
export class AiCallError extends Error {
  readonly code: AiErrorCode;
  /** Usage of the call that produced the error, when the model answered (refusal, invalid output, guard). */
  readonly usage: UsageFigures | null;
  /** For unsupported_claim: the figures and words that triggered it (never the draft text). */
  readonly detail: string[];
  constructor(
    code: AiErrorCode,
    usage: UsageFigures | null = null,
    detail: string[] = [],
  ) {
    super(code);
    this.detail = detail;
    this.name = "AiCallError";
    this.code = code;
    this.usage = usage;
  }
}

/** HTTP status of a Messages API error to its code: 401/403/404 are our configuration (key, model), not the request. */
export function aiCodeForStatus(status: number | undefined): AiErrorCode {
  if (status === 401 || status === 403 || status === 404) return "config_error";
  if (status === 429) return "rate_limited";
  if (status !== undefined && status >= 400 && status < 500)
    return "bad_request";
  return "unavailable";
}

/** The prompt parts (spec §2 caching): `stable` is the cached prefix (sales facts + rep style), never lead data. */
export type DraftPrompt = {
  language: Lang;
  stable: string;
  lead: string;
  /** The inbound email being answered, sent inside <message_du_client>; never part of `trusted`. */
  clientMessage: string | null;
  task: string;
  /** Everything the model may state as fact (stable + lead + task): the guard checks figures against it. */
  trusted: string;
  /** Owner ruling D5: the link a brochure draft must carry; a draft without it is invalid_output. */
  requiredLink?: string | null;
  /** GAVION ai follow-up: the prospect's website text, sent inside <site_web_du_prospect>; never part of `trusted`. */
  website?: string | null;
  /** GAVION ai follow-up: the answer also names its angle (follow-up touches). */
  withAngle?: boolean;
  /**
   * GAVION gavion-context: the trusted Gavion block (« Contexte Gavion » and its facts), its own cached system block
   * before `stable`; the same for every rep and lead. Its facts go to `trustedFacts`; the rest of the context is never
   * trusted.
   */
  company?: string | null;
  /**
   * GAVION gavion-context (review Important 1): the « Contexte Gavion » facts, one per item. The guard trusts their
   * figures, and their promo words only in the fact's own form (guard.ts factAllows); never part of `trusted`.
   */
  trustedFacts?: string[];
};

/** A v_timeline row (TS twin: src/components/gavion/domain/timeline.ts TimelineItem). */
export type TimelineRow = {
  id: string;
  source_id: number;
  company_id: number;
  at: string;
  kind: string;
  title: string | null;
  body_snippet: string | null;
  meta: Record<string, unknown>;
};

export type ContextInput = {
  trigger: DraftTrigger;
  lead: {
    id: number;
    name: string;
    trade: string | null;
    city: string | null;
    region: string | null;
    language: Lang;
    prospectStatus: string;
    email: string | null;
    nextCallbackAt: string | null;
  };
  contacts: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    title: string | null;
    email: string | null;
    phoneTypes: string[];
  }[];
  deal: {
    id: number;
    name: string;
    stage: string;
    amount: number | null;
    demoAt: string | null;
  } | null;
  recipient: { email: string | null; contactId: number | null };
  timeline: TimelineRow[];
  /** Full text by timeline id (notes, call notes, tasks); v_timeline itself carries 280-character snippets. */
  fullTexts: Record<string, string>;
  /** meeting_notes.id → summary_markdown (meetings and Granola call notes). */
  noteSummaries: Record<number, string>;
  objections: { objection: string; response: string }[];
  facts: {
    productBrief: string;
    brochureUrl: string | null;
    /** GAVION gavion-context: gavion.ai.company_context (or its default); absent or empty: no Gavion block. */
    companyContext?: string | null;
    /** GAVION gavion-context: gavion.ai.company_facts (or the default list): the guard's trusted Gavion figures. */
    companyFacts?: CompanyFact[];
  };
  /** Owner ruling D5: the brochure email template (rendered), the base of the after_call draft for brochure_sent. */
  brochureTemplate: {
    subject: string;
    text: string;
    url: string | null;
  } | null;
  rep: { firstName: string; lastName: string | null };
  style: { profileText: string; excerpts: string[] } | null;
  instruction: string | null;
  clientMessage: string | null;
  /** GAVION ai follow-up: the touch being written (follow_up trigger only). */
  followUp?: FollowUpFacts | null;
};
